/**
 * Proactive Token Refresh Hook
 *
 * Automatically refreshes access token every 14 minutes (before 15min expiry)
 * to ensure user never experiences 401 errors due to token expiration.
 */

import { useEffect, useRef } from 'react';
import { useAuthStore } from '../store/auth-store';
import { refreshAccessToken } from '@/src/shared/api/refreshToken';

interface UseTokenRefreshOptions {
  /**
   * Interval in milliseconds to refresh token
   * Default: 14 minutes (840,000ms)
   * Access token expires in 15 minutes, so we refresh 1 minute before
   */
  refreshInterval?: number;

  /**
   * Enable/disable automatic refresh
   * Default: true
   */
  enabled?: boolean;
}

const DEFAULT_REFRESH_INTERVAL = 14 * 60 * 1000; // 14 minutes in milliseconds

/**
 * Hook for proactive token refresh
 *
 * Usage:
 * ```tsx
 * // In your root layout or App component
 * function RootLayout() {
 *   useTokenRefresh(); // That's it!
 *   return <>{children}</>;
 * }
 * ```
 */
export function useTokenRefresh(options: UseTokenRefreshOptions = {}) {
  const {
    refreshInterval = DEFAULT_REFRESH_INTERVAL,
    enabled = true,
  } = options;

  const { isAuthenticated } = useAuthStore();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isRefreshingRef = useRef(false);

  useEffect(() => {
    // Only run if user is authenticated and feature is enabled
    if (!isAuthenticated || !enabled) {
      // Clear existing interval if any
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    // Function to refresh token
    const performRefresh = async () => {
      // Prevent concurrent refreshes
      if (isRefreshingRef.current) {
        return;
      }

      try {
        isRefreshingRef.current = true;
        const success = await refreshAccessToken();

        if (!success) {
          console.error('[useTokenRefresh] Token refresh failed');
        }
      } catch (error) {
        console.error('[useTokenRefresh] Error during token refresh:', error);
      } finally {
        isRefreshingRef.current = false;
      }
    };

    // Set up interval for periodic refresh
    intervalRef.current = setInterval(performRefresh, refreshInterval);

    // Cleanup on unmount or when dependencies change
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isAuthenticated, enabled, refreshInterval]);

  // Return nothing (this is a side-effect only hook)
  return null;
}

/**
 * Calculate next refresh time
 * Useful for debugging
 */
export function getNextRefreshTime(intervalMs: number = DEFAULT_REFRESH_INTERVAL): Date {
  return new Date(Date.now() + intervalMs);
}
